import React, { useRef, useEffect, useState } from 'react';
import Chart from 'chart.js/auto';
import { supabaseClient } from '../../services/supabase';
import { standardLegend, standardTitle } from '../../utils/chartFormatting';

interface SolarPerCapitaComparisonChartProps {
  location: string;
  geofips: number;
}

interface PerCapitaData {
  year: number;
  locationPerCapita: number;
  californiaPerCapita: number;
  locationSectors: { [sector: string]: number };
  californiaSectors: { [sector: string]: number };
}

export const SolarPerCapitaComparisonChart: React.FC<SolarPerCapitaComparisonChartProps> = ({ location, geofips }) => {
  const chartRef = useRef<HTMLCanvasElement>(null);
  const chartInstance = useRef<Chart | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchPerCapitaData = async () => {
      if (!location || !geofips) return;

      try {
        setLoading(true);
        setError(null);

        // Fetch solar data for both location and California
        const [locationSolarData, californiaSolarData, locationPopulationData, californiaPopulationData] = await Promise.all([
          supabaseClient
            .from('all_pv_size')
            .select('year, sector, total_watts')
            .eq('geoid', geofips)
            .order('year', { ascending: true }),
          supabaseClient
            .from('all_pv_size')
            .select('year, sector, total_watts')
            .eq('geoid', 6) // California geofips
            .order('year', { ascending: true }),
          supabaseClient
            .from('population')
            .select('year, population')
            .eq('geofips', geofips)
            .order('year', { ascending: true }),
          supabaseClient
            .from('population')
            .select('year, population')
            .eq('geofips', 6) // California geofips
            .order('year', { ascending: true })
        ]);

        if (locationSolarData.error) {
          console.error('Error fetching location solar data:', locationSolarData.error);
          throw locationSolarData.error;
        }
        if (californiaSolarData.error) {
          console.error('Error fetching California solar data:', californiaSolarData.error);
          throw californiaSolarData.error;
        }
        if (locationPopulationData.error) {
          console.error('Error fetching location population data:', locationPopulationData.error);
          throw locationPopulationData.error;
        }
        if (californiaPopulationData.error) {
          console.error('Error fetching California population data:', californiaPopulationData.error);
          throw californiaPopulationData.error;
        }

        // Process location solar data by sector
        const locationYearSectorMap = new Map<number, Map<string, number>>();
        locationSolarData.data?.forEach(record => {
          const year = parseInt(record.year.toString());
          if (year > 2024) return;
          
          if (!locationYearSectorMap.has(year)) {
            locationYearSectorMap.set(year, new Map());
          }
          const sectorMap = locationYearSectorMap.get(year)!;
          const sector = record.sector || 'unknown';
          const capacity = (parseFloat(record.total_watts.toString()) || 0) / 1000;
          sectorMap.set(sector, (sectorMap.get(sector) || 0) + capacity);
        });

        // Process California solar data by sector
        const californiaYearSectorMap = new Map<number, Map<string, number>>();
        californiaSolarData.data?.forEach(record => {
          const year = parseInt(record.year.toString());
          if (year > 2024) return;
          
          if (!californiaYearSectorMap.has(year)) {
            californiaYearSectorMap.set(year, new Map());
          }
          const sectorMap = californiaYearSectorMap.get(year)!;
          const sector = record.sector || 'unknown';
          const capacity = (parseFloat(record.total_watts.toString()) || 0) / 1000;
          sectorMap.set(sector, (sectorMap.get(sector) || 0) + capacity);
        });

        // Get all sectors
        const allSectors = new Set<string>();
        locationYearSectorMap.forEach(sectorMap => {
          sectorMap.forEach((_, sector) => allSectors.add(sector));
        });
        californiaYearSectorMap.forEach(sectorMap => {
          sectorMap.forEach((_, sector) => allSectors.add(sector));
        });

        // Process population data
        const locationPopulationMap = new Map<number, number>();
        locationPopulationData.data?.forEach(record => {
          const year = parseInt(record.year.toString());
          if (year > 2024) return;
          locationPopulationMap.set(year, parseFloat(record.population.toString()) || 0);
        });

        const californiaPopulationMap = new Map<number, number>();
        californiaPopulationData.data?.forEach(record => {
          const year = parseInt(record.year.toString());
          if (year > 2024) return;
          californiaPopulationMap.set(year, parseFloat(record.population.toString()) || 0);
        });

        // Calculate cumulative capacities and per capita values by sector
        const allYears = new Set([
          ...Array.from(locationYearSectorMap.keys()),
          ...Array.from(californiaYearSectorMap.keys()),
          ...Array.from(locationPopulationMap.keys()),
          ...Array.from(californiaPopulationMap.keys())
        ]);

        const sortedYears = Array.from(allYears).sort();
        const locationSectorCumulativeMap = new Map<string, number>();
        const californiaSectorCumulativeMap = new Map<string, number>();
        allSectors.forEach(sector => {
          locationSectorCumulativeMap.set(sector, 0);
          californiaSectorCumulativeMap.set(sector, 0);
        });

        const processedData: PerCapitaData[] = sortedYears.map(year => {
          const locationYearSectorData = locationYearSectorMap.get(year) || new Map();
          const californiaYearSectorData = californiaYearSectorMap.get(year) || new Map();
          
          const locationSectors: { [sector: string]: number } = {};
          const californiaSectors: { [sector: string]: number } = {};
          
          allSectors.forEach(sector => {
            const locationSectorCapacity = locationYearSectorData.get(sector) || 0;
            const californiaSectorCapacity = californiaYearSectorData.get(sector) || 0;
            
            const locationCumulativeSectorCapacity = locationSectorCumulativeMap.get(sector)! + locationSectorCapacity;
            const californiaCumulativeSectorCapacity = californiaSectorCumulativeMap.get(sector)! + californiaSectorCapacity;
            
            locationSectorCumulativeMap.set(sector, locationCumulativeSectorCapacity);
            californiaSectorCumulativeMap.set(sector, californiaCumulativeSectorCapacity);
            
            locationSectors[sector] = locationCumulativeSectorCapacity;
            californiaSectors[sector] = californiaCumulativeSectorCapacity;
          });

          const locationPopulation = locationPopulationMap.get(year) || 0;
          const californiaPopulation = californiaPopulationMap.get(year) || 0;

          const locationPerCapita = locationPopulation > 0 ? 
            Object.values(locationSectors).reduce((sum: number, val: number) => sum + val, 0) / locationPopulation : 0;
          const californiaPerCapita = californiaPopulation > 0 ? 
            Object.values(californiaSectors).reduce((sum: number, val: number) => sum + val, 0) / californiaPopulation : 0;

          return {
            year,
            locationPerCapita,
            californiaPerCapita,
            locationSectors,
            californiaSectors
          };
        }).filter(d => d.locationPerCapita > 0 || d.californiaPerCapita > 0); // Only include years with data

        // Create chart
        if (chartRef.current && processedData.length > 0) {
          const ctx = chartRef.current.getContext('2d');
          if (ctx) {
            // Destroy existing chart if it exists
            if (chartInstance.current) {
              chartInstance.current.destroy();
            }

            chartInstance.current = new Chart(ctx, {
              type: 'line',
              data: {
                labels: processedData.map(d => d.year.toString()),
                datasets: [
                  {
                    label: `${location} Per Capita`,
                    data: processedData.map(d => d.locationPerCapita),
                    borderColor: '#3b82f6',
                    backgroundColor: 'rgba(59, 130, 246, 0.1)',
                    fill: true,
                    tension: 0.4,
                    pointRadius: 5,
                    pointHoverRadius: 7
                  },
                  {
                    label: 'California Average Per Capita',
                    data: processedData.map(d => d.californiaPerCapita),
                    borderColor: '#10b981',
                    backgroundColor: 'rgba(16, 185, 129, 0.1)',
                    fill: false,
                    tension: 0.4,
                    pointRadius: 5,
                    pointHoverRadius: 7,
                    borderDash: [5, 5]
                  }
                ]
              },
              options: {
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                  title: standardTitle(`Solar Per Capita Comparison - ${location} vs California`),
                  legend: standardLegend,
                  tooltip: {
                    callbacks: {
                      label: (context: any) => {
                        const label = context.dataset.label || '';
                        const value = context.parsed.y;
                        return `${label}: ${value.toFixed(4)} kW`;
                      }
                    }
                  }
                },
                scales: {
                  x: {
                    title: {
                      display: true,
                      text: 'Year',
                      font: { size: 14, weight: 'bold' }
                    }
                  },
                  y: {
                    title: {
                      display: true,
                      text: 'Solar Capacity Per Capita (kW)',
                      font: { size: 14, weight: 'bold' }
                    },
                    beginAtZero: true,
                    ticks: {
                      callback: (value: any) => `${value.toFixed(3)} kW`
                    }
                  }
                }
              }
            });
          }
        }

      } catch (err) {
        console.error('Error fetching per capita solar data:', err);
        setError(err instanceof Error ? err.message : 'Failed to load per capita solar data');
      } finally {
        setLoading(false);
      }
    };

    fetchPerCapitaData();

    return () => {
      if (chartInstance.current) {
        chartInstance.current.destroy();
      }
    };
  }, [location, geofips]);

  if (loading) {
    return <div>Loading solar per capita comparison data...</div>;
  }

  if (error) {
    return <div>Error: {error}</div>;
  }

  return (
    <div style={{ height: '380px', width: '100%' }}>
      <canvas ref={chartRef} />
    </div>
  );
};
